import { UserSummary } from '../../queries/users';
import { formatDateLocal } from '../Meetings/meetings-helpers';

export interface UserStats {
  totalUsers: number;
  recentUsers: number;
  marketingConsent: number;
  weeklyTips: number;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/** Registered-in-last-7-days / marketing-consent / weekly-tips counts for the stat tiles. */
export function computeUserStats(users: UserSummary[]): UserStats {
  const sevenDaysAgo = Date.now() - SEVEN_DAYS_MS;
  return {
    totalUsers: users.length,
    recentUsers: users.filter((u) => new Date(u.created_at).getTime() >= sevenDaysAgo).length,
    marketingConsent: users.filter((u) => u.marketing_consent).length,
    weeklyTips: users.filter((u) => u.weekly_tips_subscribed).length,
  };
}

export interface DailySignup {
  date: string;
  count: number;
}

/** One bucket per day for the last 7 days (oldest first), counting signups on each day. */
export function computeDailySignups(users: UserSummary[]): DailySignup[] {
  const buckets = new Map<string, number>();
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    buckets.set(formatDateLocal(date), 0);
  }

  for (const user of users) {
    const day = formatDateLocal(new Date(user.created_at));
    if (buckets.has(day)) buckets.set(day, (buckets.get(day) ?? 0) + 1);
  }

  return Array.from(buckets.entries()).map(([date, count]) => ({ date, count }));
}

export type TriState = 'all' | 'yes' | 'no';

export interface UserFilters {
  search: string;
  subscription: string; // '' = all
  marketing: TriState;
  weekly: TriState;
}

export const EMPTY_USER_FILTERS: UserFilters = { search: '', subscription: '', marketing: 'all', weekly: 'all' };

/** Distinct subscription levels present in the data, for the filter dropdown. */
export function subscriptionLevels(users: UserSummary[]): string[] {
  return Array.from(new Set(users.map((u) => u.subscription_level).filter(Boolean))).sort();
}

const matchesTri = (value: boolean, state: TriState) => state === 'all' || (state === 'yes') === value;

/** Client-side search + filter over the already-loaded users list. */
export function filterUsers(users: UserSummary[], filters: UserFilters): UserSummary[] {
  const term = filters.search.trim().toLowerCase();
  return users.filter((u) => {
    if (filters.subscription && u.subscription_level !== filters.subscription) return false;
    if (!matchesTri(!!u.marketing_consent, filters.marketing)) return false;
    if (!matchesTri(!!u.weekly_tips_subscribed, filters.weekly)) return false;
    if (!term) return true;
    const haystack = [u.first_name, u.last_name, u.email, u.phone_number, String(u.id)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(term);
  });
}

export type UserSortField = 'id' | 'first_name' | 'email' | 'phone_number' | 'subscription_level' | 'created_at';
export type SortDirection = 'asc' | 'desc';

/** Sorts a copy of `users` by `field`; string fields localeCompare, everything else falls back to <=>. */
export function sortUsers(users: UserSummary[], field: UserSortField, direction: SortDirection): UserSummary[] {
  const sign = direction === 'asc' ? 1 : -1;
  return [...users].sort((a, b) => {
    if (field === 'created_at') {
      return sign * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }
    const aVal = a[field] ?? '';
    const bVal = b[field] ?? '';
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sign * aVal.localeCompare(bVal);
    }
    return sign * ((aVal as number) - (bVal as number));
  });
}
